import type { CSSProperties } from "react";

/**
 * Tile accent tones. Each key must resolve to a distinct CSS variable so
 * adjacent Updates category tiles stay distinguishable across themes.
 */
export type ToneKey =
  | "primary"
  | "cobalt"
  | "plum"
  | "moss"
  | "clay"
  | "rose"
  | "slate"
  | "ochre"
  | "ai"
  | "action"
  | "actionDeep"
  | "warning"
  | "success"
  | "danger"
  | "chart3"
  | "brown"
  | "muted";

export const TONE_TOKENS: Record<ToneKey, { primary: string; strength: string }> = {
  primary: { primary: "--primary", strength: "88%" },
  cobalt: { primary: "--cobalt", strength: "88%" },
  plum: { primary: "--plum", strength: "86%" },
  moss: { primary: "--moss", strength: "86%" },
  clay: { primary: "--clay", strength: "86%" },
  rose: { primary: "--rose", strength: "86%" },
  slate: { primary: "--slate", strength: "78%" },
  ochre: { primary: "--ochre", strength: "86%" },
  ai: { primary: "--ai", strength: "86%" },
  action: { primary: "--action", strength: "86%" },
  /** Deeper Ask / pathway fill — same --action hue, fuller mix for contrast. */
  actionDeep: { primary: "--action", strength: "100%" },
  warning: { primary: "--warning", strength: "86%" },
  success: { primary: "--success", strength: "86%" },
  danger: { primary: "--destructive", strength: "88%" },
  chart3: { primary: "--chart-3", strength: "86%" },
  brown: { primary: "--muted-foreground", strength: "68%" },
  muted: { primary: "--muted", strength: "55%" },
};

/** Map Tailwind / theme bg utility → tone. Prefer identity keys from design.md. */
export const BG_TO_TONE: Record<string, ToneKey> = {
  "bg-primary": "primary",
  "bg-cobalt": "cobalt",
  "bg-plum": "plum",
  "bg-moss": "moss",
  "bg-clay": "clay",
  "bg-rose": "rose",
  "bg-slate": "slate",
  "bg-ochre": "ochre",
  "bg-ai": "ai",
  "bg-action": "action",
  "bg-warning": "warning",
  "bg-success": "success",
  "bg-destructive": "danger",
  "bg-chart-3": "chart3",
  "bg-muted": "muted",

  // Notable hero and legacy palette utilities.
  "bg-violet-500": "primary",
  "bg-sky-500": "cobalt",
  "bg-fuchsia-500": "plum",
  "bg-lime-500": "moss",
  "bg-orange-500": "warning",
  "bg-amber-500": "ochre",
  "bg-yellow-500": "ochre",
  "bg-pink-500": "rose",
  "bg-purple-500": "ai",
  "bg-indigo-500": "ai",
  "bg-teal-500": "action",
  "bg-teal-700": "actionDeep",
  "bg-emerald-500": "success",
  "bg-emerald-600": "success",
  "bg-red-500": "danger",
  "bg-rose-500": "rose",
  "bg-slate-400": "slate",
  "bg-slate-500": "slate",
  "bg-stone-500": "brown",
};

export const TILE_SURFACE_FILL_CLS =
  "bg-[color-mix(in_oklab,var(--tile-accent)_var(--tile-strength),hsl(var(--card)))] text-foreground";

export const TILE_SURFACE_CLS =
  `${TILE_SURFACE_FILL_CLS} ring-1 ring-inset ring-white/10`;

export const TILE_MUTED_SURFACE_CLS =
  "bg-muted/40 text-foreground ring-1 ring-inset ring-border/50";

export function toneStyle(bgClass: string): CSSProperties | undefined {
  const tone = BG_TO_TONE[bgClass];
  if (!tone) return undefined;
  const definition = TONE_TOKENS[tone];
  return {
    ["--tile-accent" as string]: `hsl(var(${definition.primary}))`,
    ["--tile-strength" as string]: definition.strength,
  };
}
